import type {LayoutPayload} from '@github-ui/code-view-types'
import type {DataRouterApplicationBuilder} from '@github-ui/react-core/future/data-router-application'
import {mainQuery} from '@github-ui/react-core/future/main-query'

import {codeViewAppBuilder} from '../config/app-builder'
import {budgetThresholdBannerQuery} from '../queries/budget-threshold-banner-query'

type UIServiceStyleSheetUrls = {code: string; global: string}

export function buildCodeViewLayoutRoute(
  appBuilder: DataRouterApplicationBuilder<string>,
  stylesheetUrls?: UIServiceStyleSheetUrls,
) {
  return appBuilder.createQueryRouteConfig('codeViewLayoutRoute', {
    path: '/:owner/:repo',
    queries: [
      mainQuery<LayoutPayload>({
        queryDeps: ({pathname}) => ({pathname: `${pathname}/_layout`}),
      }),
      budgetThresholdBannerQuery,
    ],
    shouldRevalidate: ({currentParams, nextParams}) => {
      return currentParams.owner !== nextParams.owner || currentParams.repo !== nextParams.repo
    },
    uiServiceOptions: {
      gates: [
        {flag: 'ui_route_code_view', onFail: {passthroughId: 'code_view'}},
        {holdback: true, onFail: {passthroughId: 'code_view'}},
      ],
      tsrConfig: {
        // React Router's shouldRevalidate can't be automatically adapted for TanStack Router.
        // That's fine here: it only guards param changes, and TSR reloads on param change automatically.
        // This noop satisfies the TSR adapter's requirement that shouldRevalidate be manually reviewed.
        shouldReload: undefined,
        // Code view pages (overview, blob, tree, blame, etc.) omit the site footer to match
        // the Rails version.
        staticData: {hideFooter: true},
        // Force React to remount the CodeViewLayout component tree when the repo
        // changes. Without this, cross-repo soft-navigations (e.g. fork → parent)
        // reuse the same component instance and providers retain stale state.
        remountDeps: ({params}) => ({owner: params.owner, repo: params.repo}),
        ...(stylesheetUrls && {
          head: () => ({
            links: [
              {rel: 'stylesheet', href: stylesheetUrls.global, crossorigin: 'anonymous'},
              {rel: 'stylesheet', href: stylesheetUrls.code, crossorigin: 'anonymous'},
            ],
          }),
        }),
      },
    },
  })
}

export const codeViewLayoutRoute = buildCodeViewLayoutRoute(codeViewAppBuilder)

// UI Service supplies build-resolved stylesheet URLs so this route can express its dependencies without Vite coupling.
export function buildCodeViewLayoutRouteForUIService(stylesheetUrls: UIServiceStyleSheetUrls) {
  return buildCodeViewLayoutRoute(codeViewAppBuilder, stylesheetUrls)
}
